import { createDebugger } from '../debug.js';

/**
 * Custom error class for lightbox-specific errors
 */
class LightboxError extends Error {
    constructor(message, code, originalError) {
        super(message);
        this.name = 'LightboxError';
        this.code = code;
        this.originalError = originalError;
    }
}

// HLS.js for HLS video support - dynamically imported when needed
let Hls = null;

const DEBUG = false;

/**
 * CustomLightbox - A class-based PhotoSwipe lightbox implementation
 *
 * Usage: new CustomLightbox(options)
 *
 * Provides overridable methods for customization:
 * - contentLoad(e): Handle content loading
 * - contentActivate(e): Handle content activation
 * - contentDeactivate(e): Handle content deactivation
 * - createLightboxVideoElement(videoSrc): Create video elements
 */
export class CustomLightbox {
    /**
     * Constants for configuration and validation
     */
    static get CONSTANTS() {
        return {
            VIDEO_READY_STATE: {
                HAVE_NOTHING: 0,
                HAVE_METADATA: 1,
                HAVE_CURRENT_DATA: 2,
                HAVE_FUTURE_DATA: 3,
                HAVE_ENOUGH_DATA: 4
            },
            VIDEO_ERROR_CODES: {
                MEDIA_ERR_ABORTED: 1,
                MEDIA_ERR_NETWORK: 2,
                MEDIA_ERR_DECODE: 3,
                MEDIA_ERR_SRC_NOT_SUPPORTED: 4
            },
            DEFAULTS: {
                LAZY_LOAD_DELAY: 50,
                BG_OPACITY: 1,
                MAX_DIMENSION: 8192,
                MIN_DIMENSION: 1
            },
            PROTOCOLS: {
                ALLOWED: ['http:', 'https:', 'data:']
            },
            ERROR_CODES: {
                INIT_FAILED: 'INIT_FAILED',
                INVALID_OPTIONS: 'INVALID_OPTIONS',
                MODULE_LOAD_FAILED: 'MODULE_LOAD_FAILED',
                PHOTOSWIPE_NOT_FOUND: 'PHOTOSWIPE_NOT_FOUND',
                VIDEO_LOAD_FAILED: 'VIDEO_LOAD_FAILED',
                INVALID_URL: 'INVALID_URL'
            }
        };
    }
    constructor(options = {}) {
        this.options = this.mergeDefaultOptions(options);
        this.validateOptions();

        this.initialized = false;
        this.lightbox = null;
        this.elements = [];
        this.clickListeners = new Map();
        this.currentActiveVideo = null;
        this.videoListeners = new WeakMap(); // Track video event listeners for cleanup
        this.activeVideoPromise = null; // Track active video promise to prevent race conditions
        this.cachedElements = null; // Cache DOM elements for performance
        this.hlsInstances = new WeakMap(); // Track HLS instances for each video

        this.debug = createDebugger('lightbox-custom', this.options.debug && DEBUG);

        this.debug.log('CustomLightbox initialized with options:', this.options);
    }

    /**
     * Default options for the lightbox
     */
    getDefaultOptions() {
        const { DEFAULTS } = CustomLightbox.CONSTANTS;
        return {
            childSelector: null,
            gallerySelector: null,
            mainClass: '',
            addListener: false,
            debug: false,
            showHideAnimationType: 'zoom',
            bgOpacity: DEFAULTS.BG_OPACITY,
            lazyLoadDelay: DEFAULTS.LAZY_LOAD_DELAY
        };
    }

    /**
     * Merge user options with defaults
     */
    mergeDefaultOptions(userOptions) {
        return { ...this.getDefaultOptions(), ...userOptions };
    }

    /**
     * Validate required options
     */
    validateOptions() {
        const { ERROR_CODES } = CustomLightbox.CONSTANTS;

        if (!this.options.childSelector) {
            throw new LightboxError('childSelector is required', ERROR_CODES.INVALID_OPTIONS);
        }
        if (!this.options.gallerySelector) {
            throw new LightboxError('gallerySelector is required', ERROR_CODES.INVALID_OPTIONS);
        }
    }

    /**
     * Get theme icons (with fallback)
     */
    getIcons() {
        return window.theme?.icons || {};
    }

    /**
     * Validate and sanitize dimensions
     */
    validateDimensions(width, height) {
        const { DEFAULTS } = CustomLightbox.CONSTANTS;

        return {
            width: Math.max(DEFAULTS.MIN_DIMENSION, Math.min(DEFAULTS.MAX_DIMENSION, width || 0)),
            height: Math.max(DEFAULTS.MIN_DIMENSION, Math.min(DEFAULTS.MAX_DIMENSION, height || 0))
        };
    }

    /**
     * Sanitize and validate URLs
     */
    sanitizeUrl(url) {
        if (!url || typeof url !== 'string') return null;

        try {
            const urlObj = new URL(url, window.location.origin);
            const { PROTOCOLS } = CustomLightbox.CONSTANTS;

            // Only allow whitelisted protocols
            if (!PROTOCOLS.ALLOWED.includes(urlObj.protocol)) {
                console.warn('Invalid URL protocol:', urlObj.protocol);
                return null;
            }
            return urlObj.href;
        } catch (error) {
            console.warn('Invalid URL:', url, error);
            return null;
        }
    }

    /**
     * Sanitize text to prevent XSS
     */
    sanitizeText(text) {
        if (!text || typeof text !== 'string') return '';

        // Basic XSS prevention for alt text
        return text.replace(/[<>&"']/g, (char) => {
            const entities = {
                '<': '&lt;',
                '>': '&gt;',
                '&': '&amp;',
                '"': '&quot;',
                "'": '&#x27;'
            };
            return entities[char];
        });
    }

    /**
     * Initialize the lightbox
     */
    async init() {
        if (this.initialized) {
            this.debug.log('Already initialized, skipping...');
            return this;
        }

        try {
            await this.setupLightbox();
            this.initialized = true;
            this.debug.log('Lightbox initialized successfully');
            return this;
        } catch (error) {
            const { ERROR_CODES } = CustomLightbox.CONSTANTS;
            const lightboxError = new LightboxError(
                `Failed to initialize lightbox: ${error.message}`,
                ERROR_CODES.INIT_FAILED,
                error
            );

            console.error('CustomLightbox initialization failed:', lightboxError);

            // Cleanup on failure
            this.destroy();

            throw lightboxError;
        }
    }

    /**
     * Setup the PhotoSwipe lightbox
     */
    async setupLightbox() {
        // Find lightbox elements
        this.elements = document.querySelectorAll(this.options.childSelector);
        if (!this.elements.length) {
            this.debug.log('No lightbox elements found with selector:', this.options.childSelector);
            return;
        }

        // Create data source (optimized for performance)
        const dataSource = [];
        for (let i = 0; i < this.elements.length; i++) {
            dataSource.push(this.createDataSourceItem(this.elements[i]));
        }
        this.debug.log('DataSource:', dataSource);

        // Load PhotoSwipe module
        const pswpModule = await this.loadPhotoSwipeModule();
        const { PhotoSwipeLightbox, PhotoSwipe } = pswpModule;

        if (!PhotoSwipeLightbox || !PhotoSwipe) {
            const { ERROR_CODES } = CustomLightbox.CONSTANTS;
            throw new LightboxError(
                'PhotoSwipe modules not found in the imported chunk',
                ERROR_CODES.PHOTOSWIPE_NOT_FOUND
            );
        }

        // Configure PhotoSwipe
        const icons = this.getIcons();
        const photoSwipeConfig = {
            showHideAnimationType: this.options.showHideAnimationType,
            pswpModule: PhotoSwipe,
            mainClass: this.options.mainClass,
            bgOpacity: this.options.bgOpacity,
            arrowPrevSVG: icons.chevron,
            arrowNextSVG: icons.chevron,
            closeSVG: icons.close,
            zoomSVG: icons.zoom
        };

        // Use different configurations based on addListener option
        if (this.options.addListener) {
            // Manual mode: we handle clicks and provide dataSource
            photoSwipeConfig.dataSource = dataSource;
        } else {
            // Automatic mode: PhotoSwipe handles DOM parsing
            photoSwipeConfig.gallery = this.options.gallerySelector;
            photoSwipeConfig.children = this.options.childSelector;
        }

        this.debug.log('PhotoSwipeLightbox config:', photoSwipeConfig);
        this.debug.log(
            'Navigation arrows will be',
            dataSource.length <= 1 ? 'hidden' : 'visible',
            'for',
            dataSource.length,
            'slide(s)'
        );

        // Create lightbox instance
        this.lightbox = new PhotoSwipeLightbox(photoSwipeConfig);

        // Setup event handlers
        this.setupEventHandlers();

        // Setup click listeners if requested
        if (this.options.addListener) {
            this.setupClickListeners();
        }

        // Store dataSource length for UI button visibility logic
        this.dataSourceLength = dataSource.length;

        // Initialize PhotoSwipe
        this.lightbox.init();
    }

    /**
     * Load PhotoSwipe module dynamically
     */
    async loadPhotoSwipeModule() {
        const { ERROR_CODES } = CustomLightbox.CONSTANTS;

        if (!window.flu?.chunks?.photoswipe) {
            throw new LightboxError(
                'PhotoSwipe chunk not found in window.flu.chunks.photoswipe',
                ERROR_CODES.MODULE_LOAD_FAILED
            );
        }

        try {
            const moduleUrl = new URL(window.flu.chunks.photoswipe, window.location.origin).href;
            return await import(moduleUrl);
        } catch (error) {
            throw new LightboxError(
                `Failed to load PhotoSwipe module: ${error.message}`,
                ERROR_CODES.MODULE_LOAD_FAILED,
                error
            );
        }
    }

    /**
     * Create data source item for PhotoSwipe
     */
    createDataSourceItem(element) {
        const type = element.dataset.pswpType || 'image';
        let rawWidth = parseInt(element.dataset.pswpWidth, 10);
        let rawHeight = parseInt(element.dataset.pswpHeight, 10);

        const itemData = {
            type,
            element // Store element reference for later use (e.g., video source)
        };

        if (type === 'video') {
            // Store responsive video sources data first
            itemData.responsiveVideoData = this.extractResponsiveVideoData(element);

            // Use the appropriate responsive dimensions from the start
            const responsiveSource = this.getResponsiveVideoSource(itemData.responsiveVideoData);
            rawWidth = responsiveSource.width || rawWidth;
            rawHeight = responsiveSource.height || rawHeight;

            this.debug.log('Creating data source item for video with responsive dimensions:', {
                responsiveSource,
                width: rawWidth,
                height: rawHeight
            });
        }

        // Validate and sanitize dimensions
        const { width, height } = this.validateDimensions(rawWidth, rawHeight);

        if (width === 0 || height === 0) {
            console.warn('Lightbox element missing valid dimensions:', element);
        }

        itemData.width = width;
        itemData.height = height;

        if (type === 'video') {
            return itemData; // Video src retrieved later in contentLoad
        }

        // Sanitize and validate source URL
        const rawSrc = element.getAttribute('href') || element.dataset.pswpSrc;
        itemData.src = this.sanitizeUrl(rawSrc);

        if (!itemData.src) {
            console.warn('Invalid or missing source URL:', rawSrc);
        }

        // Safely get image data
        const img = element.querySelector('img');
        if (img) {
            itemData.msrc = this.sanitizeUrl(img.src);
            itemData.alt = this.sanitizeText(img.alt || '');
        }

        return itemData;
    }

    /**
     * Setup PhotoSwipe event handlers
     */
    setupEventHandlers() {
        this.lightbox.on('contentLoad', (e) => this.contentLoad(e));
        this.lightbox.on('contentActivate', (e) => this.contentActivate(e));
        this.lightbox.on('contentDeactivate', (e) => this.contentDeactivate(e));

        // Only handle close events - let contentActivate/Deactivate handle slide changes
        this.lightbox.on('close', (e) => this.onLightboxClose(e));

        // Handle UI button visibility on initial layout
        this.lightbox.on('initialLayout', () => {
            this.cacheUIElements();
            this.updateUIButtonsVisibility();
        });

        // Update UI button visibility when slide changes
        this.lightbox.on('change', () => {
            this.updateUIButtonsVisibility();
        });
    }

    /**
     * Extract responsive video data from element data attributes
     */
    extractResponsiveVideoData(element) {
        return {
            desktop: {
                src: element.dataset.pswpVideo,
                width: parseInt(element.dataset.pswpWidth, 10),
                height: parseInt(element.dataset.pswpHeight, 10)
            },
            mobile: element.dataset.pswpVideoMobile
                ? {
                      src: element.dataset.pswpVideoMobile,
                      width: parseInt(element.dataset.pswpWidthMobile, 10),
                      height: parseInt(element.dataset.pswpHeightMobile, 10)
                  }
                : null,
            large: element.dataset.pswpVideoLarge
                ? {
                      src: element.dataset.pswpVideoLarge,
                      width: parseInt(element.dataset.pswpWidthLarge, 10),
                      height: parseInt(element.dataset.pswpHeightLarge, 10)
                  }
                : null
        };
    }

    /**
     * Get appropriate video source for current viewport width
     */
    getResponsiveVideoSource(responsiveVideoData) {
        const width = window.innerWidth;

        // Check for mobile source (max-width: 719px)
        if (width <= 719 && responsiveVideoData.mobile) {
            return responsiveVideoData.mobile;
        }
        // Check for large source (min-width: 1800px)
        else if (width >= 1800 && responsiveVideoData.large) {
            return responsiveVideoData.large;
        }
        // Default to desktop source
        return responsiveVideoData.desktop;
    }

    /**
     * Setup click listeners for lightbox elements
     */
    setupClickListeners() {
        this.elements.forEach((element, index) => {
            const clickHandler = async (e) => {
                e.preventDefault();

                // Handle lazy-loaded images
                await this.handleLazyImages(element);

                this.lightbox.loadAndOpen(index);
            };

            element.addEventListener('click', clickHandler);
            this.clickListeners.set(element, clickHandler);

            // Listen for responsive source changes from the responsive-video component
            if (element.dataset.pswpType === 'video') {
                element.addEventListener('responsive-source-change', (e) => {
                    this.debug.log('Responsive source change detected:', e.detail);
                    // Update the data source with new responsive video data
                    if (this.lightbox && this.lightbox.pswp) {
                        this.updateCurrentVideoSource(index, e.detail);
                    }
                });
            }
        });
    }

    /**
     * Cache UI element references for performance
     */
    cacheUIElements() {
        if (!this.cachedElements) {
            const pswpElement = this.lightbox.pswp.element;
            this.cachedElements = {
                pswpElement,
                prevArrow: pswpElement.querySelector('.pswp__button--arrow--prev'),
                nextArrow: pswpElement.querySelector('.pswp__button--arrow--next'),
                zoomButton: pswpElement.querySelector('.pswp__button--zoom')
            };
        }
    }

    /**
     * Update all UI button visibility based on current state
     * Handles:
     * - Navigation arrows (hidden for single slide lightboxes)
     * - Zoom button (hidden for video content)
     */
    updateUIButtonsVisibility() {
        if (!this.lightbox?.pswp || !this.cachedElements) return;

        try {
            const { prevArrow, nextArrow, zoomButton } = this.cachedElements;

            // Hide arrows if only one slide
            const hideArrows = this.dataSourceLength <= 1;
            if (hideArrows) {
                if (prevArrow) prevArrow.classList.add('!hidden');
                if (nextArrow) nextArrow.classList.add('!hidden');
                this.debug.log('Hiding navigation arrows for single slide lightbox');
            } else {
                if (prevArrow) prevArrow.classList.remove('!hidden');
                if (nextArrow) nextArrow.classList.remove('!hidden');
            }

            // Get current slide type
            const currentSlide = this.lightbox.pswp.currSlide;
            if (!currentSlide?.data) return;

            const itemType = currentSlide.data.type || currentSlide.data.element?.dataset?.pswpType;

            // Hide zoom button for videos, show for images
            if (zoomButton) {
                if (itemType === 'video') {
                    zoomButton.classList.add('!hidden');
                    this.debug.log('Hiding zoom button for video content');
                } else {
                    zoomButton.classList.remove('!hidden');
                    this.debug.log('Showing zoom button for image content');
                }
            }
        } catch (error) {
            this.debug.log('Error updating UI button visibility:', error);
        }
    }

    /**
     * Handle lazy-loaded images before opening lightbox
     */
    async handleLazyImages(element) {
        const lazyImages = element.querySelectorAll('img[data-src], img[data-srcset]');
        if (lazyImages.length > 0 && window.setMediaSources) {
            lazyImages.forEach((img) => window.setMediaSources(img));
            await new Promise((resolve) => setTimeout(resolve, this.options.lazyLoadDelay));
        }
    }

    /**
     * OVERRIDABLE: Handle content loading
     * Override this method to customize content loading behavior
     */
    contentLoad(e) {
        const { content } = e;
        const item = content.data;

        this.debug.log('contentLoad, event passed:', e);
        this.debug.log('item:', item);

        const itemType = item.type || item.element?.dataset?.pswpType;
        this.debug.log('itemType:', itemType);

        if (itemType === 'video') {
            const videoElement = item.element;

            // Use responsive video data to get the appropriate source
            let videoSrc, videoWidth, videoHeight;

            if (item.responsiveVideoData) {
                const responsiveSource = this.getResponsiveVideoSource(item.responsiveVideoData);
                videoSrc = this.sanitizeUrl(responsiveSource.src);
                videoWidth = responsiveSource.width;
                videoHeight = responsiveSource.height;

                // Update item dimensions with responsive values
                item.width = videoWidth;
                item.height = videoHeight;
            } else {
                // Fallback to original behavior
                const rawVideoSrc = videoElement?.dataset.pswpVideo || videoElement?.getAttribute('href');
                videoSrc = this.sanitizeUrl(rawVideoSrc);
            }

            this.debug.log('Loading video', {
                videoSrc,
                videoWidth,
                videoHeight,
                responsiveVideoData: item.responsiveVideoData
            });

            if (videoElement && videoSrc) {
                e.preventDefault();
                const lightboxVideoElement = this.createLightboxVideoElement(videoSrc);
                this.debug.log('Created video element', lightboxVideoElement);
                content.element = lightboxVideoElement;

                // Store the current video source info for potential updates
                content.currentVideoSource = {
                    src: videoSrc,
                    width: videoWidth,
                    height: videoHeight
                };
            } else {
                console.warn(
                    'PhotoSwipe: Video item type detected, but failed to find valid video source:',
                    videoSrc
                );
                content.state = 'error';
            }
        } else if (itemType === 'image') {
            // For images, ensure PhotoSwipe has the data it needs
            const element = item.element;
            if (element && !item.src) {
                // PhotoSwipe didn't parse image data properly, help it
                const rawSrc = element.dataset.pswpSrc || element.getAttribute('href');
                const src = this.sanitizeUrl(rawSrc);

                if (src) {
                    item.src = src;
                    item.width = parseInt(element.dataset.pswpWidth, 10) || 1200;
                    item.height = parseInt(element.dataset.pswpHeight, 10) || 800;

                    // Set thumbnail if available
                    const img = element.querySelector('img');
                    if (img) {
                        item.msrc = this.sanitizeUrl(img.src);
                        item.alt = this.sanitizeText(img.alt || '');
                    }

                    this.debug.log('Enhanced image data:', {
                        src: item.src,
                        width: item.width,
                        height: item.height
                    });
                }
            }
        }
    }

    /**
     * OVERRIDABLE: Handle content activation
     * Override this method to customize content activation behavior
     */
    async contentActivate(e) {
        const { content } = e;
        const element = content.element;
        const item = content.data;

        const itemType = item.type || item.element?.dataset?.pswpType;

        if (itemType === 'video') {
            this.debug.log('contentActivate', e, content, element);
            const video = element?.querySelector('video[data-lightbox-video]');

            if (video) {
                content.state = 'loading';

                // Cancel previous video activation
                if (this.activeVideoPromise) {
                    this.activeVideoPromise.cancelled = true;
                }

                // Create cancellable promise
                const videoPromise = { cancelled: false };
                this.activeVideoPromise = videoPromise;

                // Get the video source (from attribute or current source)
                const videoSrc = video.getAttribute('data-video-src') || content.currentVideoSource?.src;

                // Check if this is an HLS source and browser doesn't support it natively
                const isHLS = videoSrc && videoSrc.includes('.m3u8');
                const supportsHLS =
                    video.canPlayType('application/x-mpegURL') ||
                    video.canPlayType('application/vnd.apple.mpegurl');

                if (isHLS && !supportsHLS) {
                    this.debug.log('HLS source detected, setting up HLS.js');
                    await this.setupHLS(video, videoSrc);
                } else if (isHLS || !video.src) {
                    // Set source if it's not already set (HLS on Safari or regular video)
                    video.src = videoSrc;
                }

                // Setup video events
                this.setupVideoEvents(video, content);

                // Set as current only if not cancelled
                if (!videoPromise.cancelled) {
                    this.currentActiveVideo = video;
                    this.playVideo(video);
                }
            } else {
                console.error('PhotoSwipe: Failed to find video element within the created container.');
                content.state = 'error';
                content.onError();
            }
        }
    }

    /**
     * OVERRIDABLE: Handle content deactivation
     * Override this method to customize content deactivation behavior
     */
    contentDeactivate(e) {
        const { content } = e;
        const element = content.element;
        const item = content.data;

        const itemType = item.type || item.element?.dataset?.pswpType;

        if (itemType === 'video') {
            this.debug.log('contentDeactivate', e, content, element);
            const video = element?.querySelector('video');

            if (video) {
                this.pauseVideo(video);

                // Clean up HLS instance if exists
                const hls = this.hlsInstances.get(video);
                if (hls) {
                    this.debug.log('Destroying HLS instance for deactivated video');
                    hls.destroy();
                    this.hlsInstances.delete(video);
                }

                // Clear reference if this was the active video
                if (this.currentActiveVideo === video) {
                    this.currentActiveVideo = null;
                }
            }
        }
    }

    /**
     * Load HLS.js library dynamically
     */
    async loadHLS() {
        if (Hls) return; // Already loaded

        try {
            // Dynamically import HLS.js from CDN
            const hlsModule = await import('https://cdn.skypack.dev/hls.js@latest');
            Hls = hlsModule.default;
            this.debug.log('HLS.js loaded successfully');
        } catch (error) {
            console.warn('Failed to load HLS.js:', error);
        }
    }

    /**
     * Setup HLS for a video element
     */
    async setupHLS(video, videoSrc) {
        // Clean up any existing HLS instance for this video
        const existingHls = this.hlsInstances.get(video);
        if (existingHls) {
            existingHls.destroy();
            this.hlsInstances.delete(video);
        }

        // Load HLS.js if not already loaded
        await this.loadHLS();

        if (!Hls || !Hls.isSupported()) {
            console.warn('HLS.js not available or not supported');
            // Fallback to native playback
            video.src = videoSrc;
            return false;
        }

        const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false
        });

        hls.loadSource(videoSrc);
        hls.attachMedia(video);

        // Store the HLS instance for cleanup
        this.hlsInstances.set(video, hls);

        return new Promise((resolve) => {
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                this.debug.log('HLS manifest parsed for lightbox video');
                resolve(true);
            });

            hls.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS error in lightbox:', data);
                if (data.fatal) {
                    // Fatal error, fallback to standard loading
                    hls.destroy();
                    this.hlsInstances.delete(video);
                    video.src = videoSrc;
                    resolve(false);
                }
            });
        });
    }

    /**
     * OVERRIDABLE: Create video element for lightbox
     * Override this method to customize video element creation
     */
    createLightboxVideoElement(videoSrc) {
        const container = document.createElement('div');
        container.className = 'pswp__video-wrapper';
        Object.assign(container.style, {
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#000'
        });

        const video = document.createElement('video');
        video.controls = true;
        video.playsInline = true;
        video.preload = 'metadata'; // Better for iOS - load metadata but not full video
        video.setAttribute('data-lightbox-video', 'true');
        video.setAttribute('data-video-src', videoSrc); // Store source for HLS setup

        // iOS-specific attributes for better compatibility
        video.setAttribute('webkit-playsinline', 'true'); // Legacy iOS support
        video.setAttribute('x-webkit-airplay', 'allow'); // Allow AirPlay

        Object.assign(video.style, {
            maxWidth: '100%',
            maxHeight: '100%',
            display: 'block'
        });

        // Don't set src directly if it's HLS - will be handled in contentActivate
        const isHLS = videoSrc && videoSrc.includes('.m3u8');
        if (!isHLS) {
            video.src = videoSrc;
        }

        container.appendChild(video);
        return container;
    }

    /**
     * Setup video event handlers
     */
    setupVideoEvents(video, content) {
        let loaded = false;
        let errorCount = 0;
        const MAX_ERRORS = 3; // Allow some errors before considering it fatal

        const onLoaded = () => {
            if (loaded) return;
            loaded = true;
            content.onLoaded();

            // Auto-play only if this is still the current active video and not cancelled
            if (this.currentActiveVideo === video && !this.activeVideoPromise?.cancelled) {
                this.playVideo(video);
            }
        };

        const onError = (event) => {
            // Only treat actual errors as fatal, not stalled events
            if (event.type === 'error') {
                const errorCode = video.error?.code;
                const errorMessage = video.error?.message || 'Unknown error';
                const { VIDEO_ERROR_CODES } = CustomLightbox.CONSTANTS;

                this.debug.log(`Video error (${errorCode}): ${errorMessage}`, event);

                // Network errors on mobile can be transient, give it a chance to recover
                if (errorCode === VIDEO_ERROR_CODES.MEDIA_ERR_NETWORK) {
                    errorCount++;
                    if (errorCount < MAX_ERRORS) {
                        this.debug.log(`Network error ${errorCount}/${MAX_ERRORS}, attempting recovery`);
                        // Try to reload the video
                        setTimeout(() => {
                            if (video && !video.src) return;
                            video.load();
                        }, 1000);
                        return;
                    }
                }

                // Fatal error - show PhotoSwipe error
                console.error(`PhotoSwipe: Video loading failed for src: ${video.src}`, errorMessage);
                content.onError();
            }
        };

        const onStalled = (event) => {
            // Stalled is not a fatal error, just log it
            this.debug.log('Video stalled, waiting for buffer:', video.src);
            // Don't call content.onError() - this is normal buffering behavior
        };

        const onWaiting = (event) => {
            // Waiting for data is normal, especially on mobile
            this.debug.log('Video waiting for data:', video.src);
        };

        // Store listeners for cleanup
        const listeners = {
            loadeddata: onLoaded,
            canplay: onLoaded,
            error: onError,
            stalled: onStalled,
            waiting: onWaiting
        };

        this.videoListeners.set(video, listeners);

        // Add event listeners
        Object.entries(listeners).forEach(([event, handler]) => {
            video.addEventListener(event, handler);
        });

        const { VIDEO_READY_STATE } = CustomLightbox.CONSTANTS;
        if (video.readyState >= VIDEO_READY_STATE.HAVE_FUTURE_DATA) {
            // HAVE_FUTURE_DATA or higher
            onLoaded();
        }
    }

    /**
     * Clean up video event listeners
     */
    cleanupVideoListeners(video) {
        const listeners = this.videoListeners.get(video);
        if (listeners) {
            Object.entries(listeners).forEach(([event, handler]) => {
                video.removeEventListener(event, handler);
            });
            this.videoListeners.delete(video);
        }
    }

    /**
     * Play video with proper error handling
     */
    playVideo(video) {
        if (!video) return;

        this.debug.log('Playing video:', video.src);

        // Don't try to play if already playing
        if (!video.paused) {
            this.debug.log('Video is already playing, skipping');
            return;
        }

        // If video isn't ready enough, try again soon
        const { VIDEO_READY_STATE } = CustomLightbox.CONSTANTS;
        if (video.readyState < VIDEO_READY_STATE.HAVE_FUTURE_DATA) {
            this.debug.log('Video not ready yet, will retry when loaded');
            // The setupVideoEvents will handle playing when ready
            return;
        }

        const playPromise = video.play();

        if (playPromise !== undefined) {
            playPromise
                .then(() => {
                    this.debug.log('Video play succeeded');
                })
                .catch((err) => {
                    this.debug.log('Video autoplay failed:', err);

                    // Some browsers block autoplay, try muted autoplay as fallback
                    if (!video.muted) {
                        this.debug.log('Trying muted autoplay fallback');
                        video.muted = true;

                        const mutedPlayPromise = video.play();
                        if (mutedPlayPromise !== undefined) {
                            mutedPlayPromise
                                .then(() => {
                                    this.debug.log('Muted video play succeeded');
                                })
                                .catch((mutedErr) => {
                                    this.debug.log('Muted video autoplay also failed:', mutedErr);
                                });
                        }
                    }
                });
        }
    }

    /**
     * Pause video with proper error handling
     */
    pauseVideo(video) {
        if (!video) return;

        this.debug.log('Pausing video:', video.src);

        try {
            video.pause();
        } catch (err) {
            this.debug.log('Video pause failed:', err);
        }
    }

    /**
     * Handle lightbox close - pause all videos
     */
    onLightboxClose(e) {
        this.debug.log('Lightbox close event:', e);

        // Clear reference first
        this.currentActiveVideo = null;

        // Pause all videos (no exceptions when closing)
        this.pauseAllVideos(null);
    }

    /**
     * Pause all videos in the lightbox except the current active one
     */
    pauseAllVideos(exceptVideo = null) {
        if (!this.lightbox || !this.lightbox.pswp) return;

        const videoToExclude = exceptVideo || this.currentActiveVideo;

        try {
            // Get all slides from PhotoSwipe instance
            const slides = this.lightbox.pswp.mainScroll.itemHolders;

            slides.forEach((holder) => {
                if (holder.slide && holder.slide.content && holder.slide.content.element) {
                    const videos = holder.slide.content.element.querySelectorAll('video');
                    videos.forEach((video) => {
                        // Don't pause the currently active video
                        if (video !== videoToExclude) {
                            this.pauseVideo(video);
                        }
                    });
                }
            });
        } catch (err) {
            this.debug.log('Error pausing all videos:', err);
        }
    }

    /**
     * Refresh the lightbox (re-scan for elements)
     */
    async refresh() {
        if (!this.initialized) {
            return this.init();
        }

        this.destroy();
        this.initialized = false;
        return this.init();
    }

    /**
     * Update video source for current lightbox slide
     */
    async updateCurrentVideoSource(slideIndex, responsiveData) {
        if (!this.lightbox?.pswp) return;

        try {
            const slide = this.lightbox.pswp.getSlideByIndex(slideIndex);
            if (slide && slide.content && slide.content.currentVideoSource) {
                const newSrc = this.sanitizeUrl(responsiveData.src);

                // Only update if the source actually changed
                if (slide.content.currentVideoSource.src !== newSrc) {
                    const video = slide.content.element?.querySelector('video[data-lightbox-video]');

                    if (video) {
                        const wasPlaying = !video.paused;
                        const currentTime = video.currentTime;

                        // Clean up existing HLS instance if any
                        const existingHls = this.hlsInstances.get(video);
                        if (existingHls) {
                            existingHls.destroy();
                            this.hlsInstances.delete(video);
                        }

                        // Check if new source is HLS
                        const isHLS = newSrc && newSrc.includes('.m3u8');
                        const supportsHLS =
                            video.canPlayType('application/x-mpegURL') ||
                            video.canPlayType('application/vnd.apple.mpegurl');

                        if (isHLS && !supportsHLS) {
                            // Setup HLS for new source
                            await this.setupHLS(video, newSrc);
                        } else {
                            // Update video source directly
                            video.src = newSrc;
                        }

                        // Update data attribute for future reference
                        video.setAttribute('data-video-src', newSrc);

                        // Update slide dimensions to match new video
                        if (responsiveData.width && responsiveData.height) {
                            slide.data.width = responsiveData.width;
                            slide.data.height = responsiveData.height;
                            slide.width = responsiveData.width;
                            slide.height = responsiveData.height;

                            // Force PhotoSwipe to recalculate slide dimensions
                            if (this.lightbox.pswp.currSlide === slide) {
                                this.lightbox.pswp.updateSize();
                            }
                        }

                        // Update stored info
                        slide.content.currentVideoSource = {
                            src: newSrc,
                            width: responsiveData.width,
                            height: responsiveData.height
                        };

                        // Resume playback from same position if it was playing
                        if (wasPlaying) {
                            video.currentTime = currentTime;
                            video.play().catch(() => {});
                        }

                        this.debug.log('Updated video source and dimensions for responsive breakpoint:', {
                            src: newSrc,
                            width: responsiveData.width,
                            height: responsiveData.height
                        });
                    }
                }
            }
        } catch (error) {
            this.debug.log('Error updating video source:', error);
        }
    }

    /**
     * Destroy the lightbox and cleanup
     */
    destroy() {
        // Cleanup all video listeners and HLS instances first
        if (this.lightbox?.pswp) {
            try {
                const slides = this.lightbox.pswp.mainScroll.itemHolders;
                slides.forEach((holder) => {
                    const videos = holder.slide?.content?.element?.querySelectorAll('video') || [];
                    videos.forEach((video) => {
                        // Clean up video listeners
                        this.cleanupVideoListeners(video);

                        // Clean up HLS instance if exists
                        const hls = this.hlsInstances.get(video);
                        if (hls) {
                            this.debug.log('Destroying HLS instance during cleanup');
                            hls.destroy();
                            this.hlsInstances.delete(video);
                        }
                    });
                });
            } catch (error) {
                this.debug.log('Error cleaning up video listeners and HLS:', error);
            }
        }

        if (this.lightbox) {
            try {
                this.lightbox.destroy();
            } catch (error) {
                this.debug.log('Error destroying lightbox:', error);
            }
            this.lightbox = null;
        }

        // Remove click listeners and responsive-source-change listeners
        this.clickListeners.forEach((handler, element) => {
            element.removeEventListener('click', handler);
            if (element.dataset.pswpType === 'video') {
                element.removeEventListener('responsive-source-change', this.responsiveSourceChangeHandler);
            }
        });
        this.clickListeners.clear();

        // Clear all references
        this.elements = [];
        this.currentActiveVideo = null;
        this.videoListeners = new WeakMap();
        this.hlsInstances = new WeakMap();
        this.activeVideoPromise = null;
        this.cachedElements = null;
        this.initialized = false;

        this.debug.log('Lightbox destroyed');
    }
}

// Legacy function wrapper for backward compatibility
export async function customLightbox(options) {
    const lightbox = new CustomLightbox(options);
    await lightbox.init();
    return lightbox;
}

// Export createLightboxVideoElement as standalone function for backward compatibility
export const createLightboxVideoElement = (videoSrc) => {
    const lightbox = new CustomLightbox({});
    return lightbox.createLightboxVideoElement(videoSrc);
};

// Attach to window for global access
window.CustomLightbox = CustomLightbox;
window.customLightbox = customLightbox;
